/**
 * Drag-and-Drop Hook for Skills
 * Handles reordering of skills using dnd-kit
 */

import { useState } from 'react';
import {
  DragEndEvent,
  DragStartEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { Skill } from '@/types/skills';

interface UseSkillDragDropOptions {
  onReorder: (skillIds: string[]) => void;
}

export function useSkillDragDrop({ onReorder }: UseSkillDragDropOptions) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to activate drag
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setIsDragging(true);
  };

  const handleDragEnd = (event: DragEndEvent, skills: Skill[]) => {
    const { active, over } = event;
    setActiveId(null);
    setIsDragging(false);

    if (!over || active.id === over.id) {
      return; // No change
    }

    const oldIndex = skills.findIndex((s) => s.id === active.id);
    const newIndex = skills.findIndex((s) => s.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Create new array with moved item
    const reordered = [...skills];
    const [movedSkill] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, movedSkill);

    // Return skill IDs in new order
    const skillIds = reordered.map((s) => s.id);
    onReorder(skillIds);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setIsDragging(false);
  };

  return {
    activeId,
    isDragging,
    sensors,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    collisionDetection: closestCenter,
  };
}
